import React, {useState, useContext, useEffect} from 'react';
import AppContext from '../../AppContext';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    TextField,
    Typography,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel,
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Snackbar, 
    Alert,
    Zoom,
    Fade
} from '@mui/material';
import '../../styles/appStyles.css';
import {Navigate, useNavigate} from 'react-router-dom';
import {AccountBalance, AttachMoney, CreditCard, Payments} from "@mui/icons-material";

const Payment = () => {
    const { orders, setOrders, selectedItems, setSelectedItems, code } = useContext(AppContext);
    const [tipWaiter, setTipWaiter] = useState('');
    const [tipApp, setTipApp] = useState('');
    const [includeInvoice, setIncludeInvoice] = useState(false);
    const [nip, setNip] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: '' });
    const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (!selectedItems || selectedItems.length === 0) {
        return <Navigate to="/" />;
    }

    const isValidNIP = (nip) => {
        const nipPattern = /^\d{10}$/;
        return nipPattern.test(nip);
    };

    const handleSelectItem = (id) => {
        const updatedItems = selectedItems.map(item =>
            item.id === id
                ? {...item, isSelected: !item.isSelected, quantityToPay: !item.isSelected ? item.quantity : 0}
                : item
        );
        setSelectedItems(updatedItems);
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleItemChange = (id, quantity) => {
        const updatedItems = selectedItems.map(item =>
            item.id === id ? {...item, quantityToPay: Math.min(item.quantity, Math.max(quantity, 1))} : item
        );
        setSelectedItems(updatedItems);
    };

    const handleSelectAllItems = () => {
        const areAllSelected = selectedItems.every(item => item.isSelected);
        const updatedItems = selectedItems.map(item => ({
            ...item,
            isSelected: !areAllSelected,
            quantityToPay: !areAllSelected ? item.quantity : 0
        }));
        setSelectedItems(updatedItems);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
    };

    const handleConfirmPayment = () => {
        const updatedOrders = orders.map(item => {
            const selectedItem = selectedItems.find(selected => selected.id === item.id);
            if (selectedItem && selectedItem.quantityToPay > 0) {
                const remainingQuantity = item.quantity - selectedItem.quantityToPay;

                if (remainingQuantity > 0) {
                    return { ...item, quantity: remainingQuantity, isSelected: false };
                } else {
                    return null;
                }
            }
            return item;
        }).filter(item => item !== null);

        setOrders(updatedOrders);
        setOpenDialog(false);
        navigate('/');
    };

    const totalPrice = selectedItems.reduce((sum, item) => sum + (item.isSelected ? item.price * item.quantityToPay : 0), 0);
    const totalTip = (tipWaiter ? parseFloat(tipWaiter) : 0) + (tipApp ? parseFloat(tipApp) : 0);
    const totalAmount = totalPrice + totalTip;

    const handlePayment = async () => {
        const isAnyItemSelected = selectedItems.filter(item => item.isSelected && item.quantityToPay > 0).length > 0;
        if (!isAnyItemSelected) {
            setSnackbar({
                open: true,
                message: 'Proszę zaznaczyć przynajmniej jedną pozycję do zapłaty.',
                severity: 'error'
            });
            return;
        }
        if (includeInvoice && !isValidNIP(nip)) {
            setSnackbar({open: true, message: 'Proszę podać poprawny NIP!', severity: 'error'});
            return;
        }
        if (!paymentMethod) {
            setSnackbar({
                open: true,
                message: 'Proszę wybrać metodę płatności.',
                severity: 'error'
            });
            return;
        }

        const summaryItems = selectedItems
            .filter(item => item.isSelected && item.quantityToPay > 0)
            .map(item => ({
                id: item.id,
                quantity: item.quantityToPay,
            }));
        /*setDialogMessage(paymentSummary);
        setOpenDialog(true);*/
        const encodedCode = encodeURIComponent(code);
        const response = await fetch(`${baseUrl}/api/customers/pay?code=${encodedCode}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                items: summaryItems,
                paymentMethod: paymentMethod,
                nip: nip,
                isNip: includeInvoice,
                tipForWaiter: tipWaiter,
                tipForDeveloper: tipApp
            }),
        });
        if (response.ok) {
            setSnackbar({
                open: true,
                message: 'Informacja została przekazana do kelnera.',
                severity: 'success'
            });
            setTimeout(() => navigate('/'), 2000);
        } else {
            setSnackbar({
                open: true,
                message: 'Wystąpił błąd podczas przetwarzania płatności.',
                severity: 'error'
            });
        }
    };

    const areAllItemsSelected = selectedItems.every(item => item.isSelected);

    return (
        <Box>
            <Fade in={true} timeout={800}>
                <Paper 
                    elevation={3} 
                    sx={{ 
                        p: 3, 
                        mb: 4, 
                        borderRadius: '15px',
                        background: 'linear-gradient(135deg, #fff, #f9f9f9)' 
                    }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                        <Typography 
                            variant="h4" 
                            sx={{
                                fontWeight: 'bold',
                                backgroundImage: 'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
                                backgroundClip: 'text',
                                textFillColor: 'transparent',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <AccountBalance sx={{ mr: 1, fontSize: '2rem' }} />
                            Płatności
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={handleSelectAllItems}
                            sx={{ 
                                borderRadius: '50px',
                                px: 3,
                                py: 1,
                                background: 'var(--secondary-color)',
                                '&:hover': {
                                    background: 'var(--secondary-color)',
                                    filter: 'brightness(1.1)'
                                }
                            }}
                        >
                            {areAllItemsSelected ? 'Odznacz wszystko' : 'Wybierz wszystkie pozycje'}
                        </Button>
                    </Box>
                </Paper>
            </Fade>

                            <Grid container>
                {selectedItems.map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                        <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                            <Paper 
                                className="cart-item-card"
                                sx={{
                                    borderLeft: item.isSelected ? '4px solid var(--success-color)' : '4px solid transparent',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {item.imageUrl && (
                                <Box>
                                    <img 
                                        src={item.imageUrl} 
                                        alt={item.name} 
                                        className="cart-image"
                                    />
                                </Box>
                                )}
                                <Box className="cart-item-info">
                                    <Typography className="cart-item-title">{item.name}</Typography>
                                    <Typography 
                                        variant="body2"
                                        sx={{ 
                                            color: 'var(--primary-color)', 
                                            fontWeight: 600 
                                        }}
                                    >
                                        {item.price} zł
                                    </Typography>
                                    <Typography 
                                        variant="body2"
                                        sx={{ color: 'var(--dark-color)', opacity: 0.7 }}
                                    >
                                        Całkowita ilość: <span style={{ fontWeight: 600 }}>{item.quantity}</span>
                                    </Typography>

                                    <FormControlLabel
                                        control={
                                            <Checkbox 
                                                checked={item.isSelected || false}
                                                onChange={() => handleSelectItem(item.id)}
                                                sx={{
                                                    color: 'var(--primary-color)',
                                                    '&.Mui-checked': { color: 'var(--success-color)' }
                                                }}
                                            />
                                        }
                                        label="Wybierz do zapłaty"
                                    />

                                    {item.isSelected && (
                                        <Fade in={true}>
                                            <Box 
                                                className="cart-quantity-controls"
                                                sx={{
                                                    mt: 1,
                                                    p: 1, 
                                                    backgroundColor: 'rgba(0,0,0,0.03)', 
                                                    borderRadius: '10px' 
                                                }}
                                            >
                                                <Typography sx={{ mr: 1 }}>Ilość:</Typography>
                                                <button 
                                                    className="quantity-btn"
                                                    onClick={() => handleItemChange(item.id, item.quantityToPay - 1)}
                                                    disabled={item.quantityToPay <= 1}
                                                    style={{ transition: 'all 0.1s ease' }}
                                                >
                                                    -
                                                </button>
                                                <span className="quantity-value">{item.quantityToPay}</span>
                                                <button 
                                                    className="quantity-btn"
                                                    onClick={() => handleItemChange(item.id, item.quantityToPay + 1)}
                                                    disabled={item.quantityToPay >= item.quantity}
                                                    style={{ transition: 'all 0.1s ease' }}
                                                >
                                                    +
                                                </button>
                                            </Box>
                                        </Fade>
                                    )}
                                </Box>
                            </Paper>
                        </Zoom>
                    </Grid>
                ))}
            </Grid>

            <Fade in={true} timeout={1000}>
                <Paper 
                    elevation={3} 
                    sx={{
                        mt: 3,
                        p: 3, 
                        borderRadius: '15px',
                        background: 'linear-gradient(135deg, #fff, #f9f9f9)' 
                    }}
                >
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            mb: 3,
                            fontWeight: 'bold',
                            color: 'var(--dark-color)',
                            borderBottom: '2px solid var(--primary-color)',
                            pb: 1,
                            width: 'fit-content'
                        }}
                    >
                        Napiwki
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Napiwek dla kelnera"
                                value={tipWaiter}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (/^\d*\.?\d{0,2}$/.test(value)) {
                                        setTipWaiter(value);
                                    }
                                }}
                                placeholder="0 zł"
                                type="number"
                                InputProps={{endAdornment: 'zł'}}
                                fullWidth
                                sx={{ 
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                        backgroundColor: 'white'
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Napiwek dla twórcy aplikacji"
                                value={tipApp}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (/^\d*\.?\d{0,2}$/.test(value)) {
                                        setTipApp(value);
                                    }
                                }}
                                placeholder="0 zł"
                                type="number"
                                InputProps={{endAdornment: 'zł'}}
                                fullWidth
                                sx={{ 
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                        backgroundColor: 'white'
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Box mt={4} sx={{ textAlign: 'center' }}>
                        <Typography 
                            variant="h5"
                            sx={{ 
                                mb: 1,
                                color: 'var(--dark-color)',
                                fontWeight: 500
                            }}
                        >
                            Całkowita kwota do zapłaty
                        </Typography>
                        <Typography 
                            variant="h3"
                            sx={{ 
                                fontWeight: 'bold',
                                color: 'var(--primary-color)',
                                backgroundImage: 'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
                                backgroundClip: 'text',
                                textFillColor: 'transparent',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            {totalAmount.toFixed(2)} zł
                        </Typography>
                    </Box>
                </Paper>
            </Fade>

            <Fade in={true} timeout={1200}>
                <Paper 
                    elevation={3} 
                    sx={{ 
                        mt: 4, 
                        p: 3, 
                        borderRadius: '15px',
                        background: 'linear-gradient(135deg, #fff, #f9f9f9)' 
                    }}
                >
                    <Box mb={3}>
                        <Typography 
                            variant="h5" 
                            sx={{ 
                                mb: 2,
                                fontWeight: 'bold',
                                color: 'var(--dark-color)',
                                borderBottom: '2px solid var(--primary-color)',
                                pb: 1,
                                width: 'fit-content'
                            }}
                        >
                            Faktura
                        </Typography>

                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={includeInvoice} 
                                    onChange={() => setIncludeInvoice(!includeInvoice)}
                                    sx={{
                                        color: 'var(--primary-color)',
                                        '&.Mui-checked': { color: 'var(--primary-color)' }
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ fontWeight: 500 }}>Chcę fakturę</Typography>
                            }
                        />
                        {includeInvoice && (
                            <Fade in={includeInvoice}>
                                <Box mt={2} sx={{ background: 'rgba(0,0,0,0.03)', p: 2, borderRadius: '10px' }}>
                                    <TextField
                                        label="NIP"
                                        value={nip}
                                        onChange={(e) => setNip(e.target.value)}
                                        error={includeInvoice && !isValidNIP(nip)}
                                        helperText={includeInvoice && !isValidNIP(nip) ? 'NIP musi składać się z 10 cyfr' : ''}
                                        fullWidth
                                        sx={{ 
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '10px',
                                                backgroundColor: 'white'
                                            }
                                        }}
                                    />
                                </Box>
                            </Fade>
                        )}
                    </Box>

                    <Box mt={4}>
                        <Typography 
                            variant="h5" 
                            sx={{ 
                                mb: 3,
                                fontWeight: 'bold',
                                color: 'var(--dark-color)',
                                borderBottom: '2px solid var(--primary-color)',
                                pb: 1,
                                width: 'fit-content'
                            }}
                        >
                            Metoda płatności
                        </Typography>

                        <FormControl component="fieldset" fullWidth>
                            <RadioGroup 
                                value={paymentMethod} 
                                onChange={(e) => setPaymentMethod(e.target.value)}
                            >
                                <Paper 
                                    className={`payment-option ${paymentMethod === 'WAITER_CARD' ? 'payment-option-selected' : ''}`}
                                    elevation={2}
                                    onClick={() => setPaymentMethod('WAITER_CARD')}
                                >
                                    <FormControlLabel 
                                        value="WAITER_CARD" 
                                        control={<Radio sx={{ color: 'var(--primary-color)', '&.Mui-checked': { color: 'var(--primary-color)' } }}/>} 
                                        label={
                                            <Box display="flex" alignItems="center">
                                                <CreditCard className="payment-icon"/>
                                                <Typography sx={{ fontWeight: 500 }}>Zapłać kelnerowi kartą</Typography>
                                            </Box>
                                        }
                                        sx={{ width: '100%', m: 0 }}
                                    />
                                </Paper>

                                <Paper 
                                    className={`payment-option ${paymentMethod === 'WAITER_CASH' ? 'payment-option-selected' : ''}`}
                                    elevation={2}
                                    onClick={() => setPaymentMethod('WAITER_CASH')}
                                    sx={{ mt: 2 }}
                                >
                                    <FormControlLabel 
                                        value="WAITER_CASH" 
                                        control={<Radio sx={{ color: 'var(--primary-color)', '&.Mui-checked': { color: 'var(--primary-color)' } }}/>} 
                                        label={
                                            <Box display="flex" alignItems="center">
                                                <AttachMoney className="payment-icon"/>
                                                <Typography sx={{ fontWeight: 500 }}>Zapłać kelnerowi gotówką</Typography>
                                            </Box>
                                        }
                                        sx={{ width: '100%', m: 0 }}
                                    />
                                </Paper>

                                {/*<FormControlLabel value="ONLINE" control={<Radio/>} label={
                                    <Box display="flex" alignItems="center">
                                        <AccountBalance style={{marginRight: '8px'}}/>
                                        <Typography>Zapłać przez przelewy 24</Typography>
                                    </Box>
                                }/>*/}
                            </RadioGroup>
                        </FormControl>
                    </Box>
                </Paper>
            </Fade>

            <Box mt={5} display="flex" justifyContent="center">
                <Zoom in={true} timeout={1200}>
                    <Button 
                        variant="contained" 
                        className="action-button primary-button"
                        onClick={handlePayment}
                        startIcon={<Payments />}
                        sx={{ 
                            fontSize: '1.2rem',
                            py: 1.5,
                            px: 5,
                            minWidth: '250px',
                            color: 'white !important',
                            textShadow: '0px 1px 2px rgba(0, 0, 0, 0.3)'
                        }}
                    >
                        Zapłać
                    </Button>
                </Zoom>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    className="toast-notification"
                    sx={{ 
                        background: snackbar.severity === 'success' 
                            ? 'linear-gradient(135deg, var(--success-color), #7bed9f)' 
                            : 'linear-gradient(135deg, var(--error-color), #ff6b81)',
                        color: 'white',
                        fontWeight: 'bold',
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem'
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

                            <Dialog 
                open={openDialog} 
                onClose={handleDialogClose} 
                maxWidth="md" 
                fullWidth
                PaperProps={{ 
                    className: "custom-dialog",
                    sx: { borderRadius: '20px', overflow: 'hidden' }
                }}
                            >
                <DialogTitle className="dialog-title">Podsumowanie płatności</DialogTitle>
                <DialogContent className="dialog-content">
                    {dialogMessage.summary && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Pozycje do opłacenia:
                            </Typography>
                            <Grid container spacing={2}>
                                {dialogMessage.summary.map((item, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Paper style={{padding: '10px', display: 'flex', alignItems: 'center'}}>
                                            <Box>
                                                <img
                                                    src={item.imageUrl || 'placeholder-image.jpg'} // Obraz lub domyślny
                                                    alt={item.title}
                                                    style={{
                                                        width: '60px',
                                                        height: '60px',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px',
                                                        marginRight: '10px'
                                                    }}
                                                />
                                            </Box>
                                            <Box>
                                                <Typography variant="h6">{item.title}</Typography>
                                                <Typography variant="body2">Ilość: {item.quantityToPay}</Typography>
                                                <Typography variant="body2">
                                                    Kwota: {item.totalPrice.toFixed(2)} zł
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                            <Typography variant="body2" mt={3}>
                                Metoda płatności: {dialogMessage.paymentMethod}
                            </Typography>
                            <Typography variant="body2" mt={1}>
                                Łączna kwota: {(dialogMessage.summary.reduce((acc, item) => acc + item.totalPrice, 0) + totalTip).toFixed(2)} zł
                            </Typography>
                            <Typography variant="h6" mt={3} gutterBottom style={{ textAlign: 'center' }}>
                                Czy wszystko się zgadza?
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions className="dialog-actions" sx={{ justifyContent: 'center', mt: 2 }}>
                    <Button 
                        onClick={handleConfirmPayment} 
                        className="action-button primary-button"
                        sx={{ 
                            borderRadius: '50px', 
                            px: 4,
                            py: 1
                        }}
                    >
                        Tak
                    </Button>
                    <Button 
                        onClick={handleDialogClose} 
                        className="action-button error-button"
                        sx={{ 
                            borderRadius: '50px', 
                            px: 4,
                            py: 1
                        }}
                    >
                        Nie
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Payment;